import { useEffect, useRef, RefObject } from "react";
import { VirtuosoHandle } from "react-virtuoso";

import { Variable } from "@/components/InputVariableConfigurator/types";

interface UseVariableFocusProps {
  variables: Variable[];
  virtuosoRef: RefObject<VirtuosoHandle>;
  scrollContainerRef: RefObject<HTMLDivElement>;
  isVirtualized: boolean;
}

export const useVariableFocus = ({
  variables,
  virtuosoRef,
  scrollContainerRef,
  isVirtualized,
}: UseVariableFocusProps) => {
  const prevVariableCount = useRef(variables.length);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Track when a new variable is added
  useEffect(() => {
    const wasAdded = variables.length > prevVariableCount.current;

    if (wasAdded && variables.length > 0) {
      const lastVariable = variables[variables.length - 1];

      // Scroll to the new variable
      if (isVirtualized && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: variables.length - 1,
          behavior: "smooth",
          align: "end",
        });
      } else if (scrollContainerRef.current) {
        // Let useAutoScroll handle non-virtualized scrolling
        // Just need to wait a bit for the element to render
        setTimeout(() => {
          const input = inputRefs.current[lastVariable.id];
          if (input) {
            input.focus();
            input.select();
          }
        }, 150);

        prevVariableCount.current = variables.length;
        return;
      }

      // Focus the input after scroll animation
      setTimeout(
        () => {
          const attemptFocus = (attempts = 0) => {
            const input = inputRefs.current[lastVariable.id];
            if (input) {
              input.focus();
              input.select();
            } else if (attempts < 5) {
              // Retry with exponential backoff for virtualized lists
              setTimeout(() => attemptFocus(attempts + 1), 100 * (attempts + 1));
            }
          };
          attemptFocus();
        },
        isVirtualized ? 400 : 150,
      );
    }

    prevVariableCount.current = variables.length;
  }, [variables, virtuosoRef, scrollContainerRef, isVirtualized]);

  const registerInputRef = (variableId: string, ref: HTMLInputElement | null) => {
    if (ref) {
      inputRefs.current[variableId] = ref;
    } else {
      delete inputRefs.current[variableId];
    }
  };

  const focusNextInput = (currentVariableId: string) => {
    const currentIndex = variables.findIndex(v => v.id === currentVariableId);
    if (currentIndex >= 0 && currentIndex < variables.length - 1) {
      const nextVariable = variables[currentIndex + 1];
      const nextInput = inputRefs.current[nextVariable.id];

      if (nextInput) {
        // If virtualized, scroll to the next item first
        if (isVirtualized && virtuosoRef.current) {
          virtuosoRef.current.scrollToIndex({
            index: currentIndex + 1,
            behavior: "smooth",
            align: "center",
          });

          // Focus after scroll animation
          setTimeout(() => {
            nextInput.focus();
            nextInput.select();
          }, 300);
        } else {
          nextInput.focus();
          nextInput.select();
        }
      }
    }
  };

  return { registerInputRef, focusNextInput };
};
